import React, { useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'

import * as yup from 'yup'
import { ViewBase } from '../../../../../components/common/ViewBase'
import { useDocumentTitle } from '../../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../../navigation/reverseDocumentTitle'
import { useCurrentSociety } from '../../../../../hooks/useCurrentSociety'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { FormControl } from '../../../../../components/common/FormControl'
import { TextInput } from '../../../../../components/common/TextInput'
import { TextArea } from '../../../../../components/common/TextArea'
import { Button, ButtonVariant } from '../../../../../components/common/Button'
import { TabBar } from '../../../../../components/common/TabBar/TabBar'
import { DropdownSelect } from '../../../../../components/common/DropdownSelect'
import { ToastType } from '../../../../../components/common/Toast/toast-type'
import { useToastNotifications } from '../../../../../hooks/useToastNotification'
import { useFormErrorMessage } from '../../../../../hooks/useFormErrorMessage'
import { createTicket } from '../../../../../api/ticket'
import { reverseUrl } from '../../../../../navigation/reverseUrl'

import {
  TicketAttachments,
  TicketAttachmentsValue,
} from '../../../../../components/ticket/TicketAttachments'
import {
  ticketReportSpace,
  TicketReportSpace,
  TicketVariant,
} from '../../../../../interfaces/models/ticket.interfaces'
import { SPACE_TRANSLATION_KEY } from '../../../../../helpers/ticketSpace'
import { NTicket } from '../../../../../interfaces/services/ticket.interfaces'
import { useTicketSettings } from '../../../../../hooks/useTicketSettings'

type CreateTicketFormValues = {
  variant: TicketVariant
  space?: TicketReportSpace
  title: string
  description?: string
}

const createTicketSchema: yup.ObjectSchema<CreateTicketFormValues> = yup
  .object({
    variant: yup
      .mixed<TicketVariant>()
      .oneOf(['question', 'report'])
      .required(),
    space: yup
      .mixed<TicketReportSpace>()
      .oneOf(ticketReportSpace as unknown as TicketReportSpace[])
      .when('variant', {
        is: 'report',
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional(),
      }),
    title: yup.string().trim().required(),
    description: yup.string().required(),
  })
  .required()

const getDefaultValues = (
  variant: 'question' | 'report'
): CreateTicketFormValues => {
  return {
    variant,
    title: '',
    description: '',
    space: 'apartment',
  }
}

const getVariantTabs = ({
  questionEnabled,
  reportEnabled,
}: {
  questionEnabled: boolean
  reportEnabled: boolean
}): TicketVariant[] => {
  const ticketVariants: TicketVariant[] = []

  if (questionEnabled) {
    ticketVariants.push('question')
  }
  if (reportEnabled) {
    ticketVariants.push('report')
  }
  return ticketVariants
}
export const TicketCreateView = (): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { getErrorMessage } = useFormErrorMessage()
  const { setToastNotification } = useToastNotifications()
  const { society } = useCurrentSociety()
  const { questionEnabled, reportEnabled } = useTicketSettings(society?._id)
  const VARIANT_TABS = getVariantTabs({ questionEnabled, reportEnabled })

  const navigate = useNavigate()

  useDocumentTitle(
    reverseDocumentTitle('society:ticket-create', {
      '{{ societyName }}': society?.name,
    })
  )

  const societyId = society?._id as string | undefined

  const [attachments, setAttachments] = useState<TicketAttachmentsValue>({
    mediaIds: [],
    documentIds: [],
    isUploading: false,
  })

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<CreateTicketFormValues>({
    mode: 'all',
    resolver: yupResolver(createTicketSchema),
    defaultValues: getDefaultValues(VARIANT_TABS[0]),
  })

  const variant = watch('variant')
  const activeTab = VARIANT_TABS.indexOf(variant)

  const handleTabChange = (index: number): void => {
    const nextVariant = VARIANT_TABS[index]
    if (!nextVariant || nextVariant === variant) return
    setValue('variant', nextVariant, { shouldValidate: true })
  }

  const spaceOptions = useMemo(
    () =>
      ticketReportSpace.map((s) => ({
        value: s,
        label: translate(SPACE_TRANSLATION_KEY[s]),
      })),
    [translate]
  )

  const { mutate: mutateCreateTicket, isPending: isCreatingTicket } =
    useMutation({
      mutationFn: async ({
        params,
        body,
      }: {
        params: NTicket.NCreateTicket.IRequestParams
        body: NTicket.NCreateTicket.IRequestBody
      }) => {
        const response = await createTicket(params, body)
        if (!response.data.data) {
          throw new Error()
        }
        return response.data.data
      },
      onSuccess: (ticket) => {
        setToastNotification(
          ToastType.SUCCESS,
          translate('ticketView.flashMessage.createTicketSuccess')
        )
        navigate(
          reverseUrl('society:ticket-detail', {
            id: societyId,
            ticketId: ticket.id,
          })
        )
      },

      onError: () => {
        setToastNotification(
          ToastType.DANGER,
          translate('ticketView.flashMessage.createTicketFailure')
        )
      },
    })

  const submitDisabled = !isValid || isCreatingTicket || attachments.isUploading

  const onSubmit = (values: CreateTicketFormValues): void => {
    if (!societyId) return

    mutateCreateTicket({
      params: { societyId },
      body: {
        title: values.title.trim(),
        message: values.description,
        mediaIds: attachments.mediaIds,
        documentIds: attachments.documentIds,
        variant: values.variant,
        ...(values.variant === 'report' && values.space
          ? { space: values.space }
          : {}),
      },
    })
  }

  const tabs = VARIANT_TABS.map((variantTab) => {
    return { title: translate(`ticketView.form.tabs.${variantTab}`) }
  })

  return (
    <ViewBase
      title={translate('ticketView.form.title')}
      className="p-6 overflow-auto"
    >
      <div>
        <div className="2xl:w-1/2">
          <TabBar
            tabs={tabs}
            activeTab={activeTab}
            setActiveTab={(value) => {
              const nextIndex =
                typeof value === 'function' ? value(activeTab) : value
              handleTabChange(nextIndex)
            }}
            disableScroll
            className="mb-4"
          />
        </div>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex w-full flex-col gap-4 2xl:w-1/2"
        >
          {variant === 'report' && (
            <Controller
              control={control}
              name="space"
              render={({ field: { value, onChange, name } }) => (
                <FormControl
                  label={translate('ticketView.form.labels.space')}
                  name={name}
                  error={errors.space && getErrorMessage(errors.space)}
                >
                  <DropdownSelect
                    value={value}
                    options={spaceOptions}
                    onChange={(next) => onChange(next as TicketReportSpace)}
                    dropdownWidth="w-full"
                    dropdownPosition="bottom-right"
                  />
                </FormControl>
              )}
            />
          )}

          <Controller
            control={control}
            name="title"
            render={({ field: { value, onChange, onBlur, name } }) => (
              <FormControl
                label={translate('ticketView.form.labels.title')}
                name={name}
                error={errors.title && getErrorMessage(errors.title)}
              >
                <TextInput
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!errors.title}
                />
              </FormControl>
            )}
          />

          <Controller
            control={control}
            name="description"
            render={({ field: { value, onChange, onBlur, name } }) => (
              <FormControl
                label={translate('ticketView.form.labels.description')}
                name={name}
                error={
                  errors.description && getErrorMessage(errors.description)
                }
              >
                <TextArea
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  rows={6}
                />
              </FormControl>
            )}
          />

          {societyId && (
            <TicketAttachments
              societyId={societyId}
              onChange={setAttachments}
            />
          )}

          <div className="flex">
            <Button
              variant={ButtonVariant.PRIMARY}
              type="submit"
              label={translate('common.actions.create')}
              disabled={submitDisabled}
              loading={isCreatingTicket}
            />
          </div>
        </form>
      </div>
    </ViewBase>
  )
}
